import { saveFile, shareFile, canShareFiles } from '../platform';
import { formatMoney } from '@vns/core';
import type { DailyReportResult } from './daily-report';
import { getCompanyDetails } from './company';

function formatDate(iso: string): string {
  if (!iso || iso === '—') return '—';
  try {
    const [y, m, d] = iso.split('-');
    if (y && m && d) return `${d}-${m}-${y}`;
    return iso;
  } catch {
    return iso;
  }
}

function formatAmount(paise: number): string {
  if (!paise || paise === 0) return '';
  return (paise / 100).toLocaleString('en-IN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

/**
 * Export Client Day Book to Excel (.xlsx)
 */
export async function exportDailyReportExcel(
  data: DailyReportResult,
  companyName = 'VNS CONSTRUCTION',
): Promise<void> {
  const XLSX = await import('xlsx');
  const { items, summary } = data;
  const company = await getCompanyDetails();

  const headerRows: unknown[][] = [
    [company.name.toUpperCase()],
    [company.legalName || 'Civil Engineering & Construction Contractors'],
    [`${company.address ? `${company.address}, ` : ''}${company.city || ''}  |  Phone: ${company.phone || ''}  |  Email: ${company.email || ''}`],
    ...(company.gstNumber ? [[`GSTIN: ${company.gstNumber}`]] : []),
    [],
    [`Client Day Book from ${formatDate(summary.fromDate)} to ${formatDate(summary.toDate)}`],
    [`Client: ${summary.clientName}   |   Project: ${summary.projectName}   |   Location: ${summary.location}`],
    [],
    ['S.No', 'Date', 'Type', 'Mode', 'Particulars and Remarks', 'Receipt Amount (₹)', 'Payable Amount (₹)', 'Running Balance (₹)'],
  ];

  const dataRows: unknown[][] = [];
  let rowIdx = 0;

  // Opening balance row if applicable
  if (summary.openingBalancePaise !== 0) {
    rowIdx += 1;
    dataRows.push([
      rowIdx,
      formatDate(summary.fromDate),
      'Opening Balance',
      '—',
      'Opening Balance (Prior Period Net Balance)',
      summary.openingBalancePaise > 0 ? summary.openingBalancePaise / 100 : '',
      summary.openingBalancePaise < 0 ? Math.abs(summary.openingBalancePaise) / 100 : '',
      summary.openingBalancePaise / 100,
    ]);
  }

  for (const item of items) {
    rowIdx += 1;
    dataRows.push([
      rowIdx,
      formatDate(item.date),
      item.typeLabel,
      item.mode || '—',
      item.particulars,
      item.receiptPaise > 0 ? item.receiptPaise / 100 : '',
      item.payablePaise > 0 ? item.payablePaise / 100 : '',
      item.runningBalancePaise / 100,
    ]);
  }

  // Summary footer rows
  dataRows.push([]);
  dataRows.push([
    '',
    '',
    '',
    '',
    'TOTAL TRANSACTIONS',
    summary.totalReceiptsPaise / 100,
    summary.totalPayablesPaise / 100,
    '',
  ]);

  const netClosing = summary.closingBalancePaise;
  const balanceLabel = netClosing >= 0 ? 'CLOSING CREDIT BALANCE (SURPLUS)' : 'CLOSING DEBIT BALANCE (PAYABLE OUTSTANDING)';
  dataRows.push([
    '',
    '',
    '',
    '',
    balanceLabel,
    '',
    Math.abs(netClosing) / 100,
    '',
  ]);

  const ws = XLSX.utils.aoa_to_sheet([...headerRows, ...dataRows]);

  // Set column widths
  ws['!cols'] = [
    { wch: 8 },  // S.No
    { wch: 14 }, // Date
    { wch: 12 }, // Type
    { wch: 14 }, // Mode
    { wch: 55 }, // Particulars
    { wch: 20 }, // Receipt
    { wch: 20 }, // Payable
    { wch: 22 }, // Running Balance
  ];

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Day Book');

  const out = XLSX.write(wb, { bookType: 'xlsx', type: 'array' }) as ArrayBuffer;
  const safeProject = (summary.projectName || 'Project').replace(/[^\w-]/g, '_');
  const safeFrom = summary.fromDate || 'AllTime';
  const safeTo = summary.toDate || 'Present';
  const fileName = `DayBook-${safeProject}-${safeFrom}_to_${safeTo}.xlsx`;

  await saveFile(
    fileName,
    new Blob([out], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }),
  );
}

/**
 * Build Client Day Book PDF document
 */
async function buildDailyReportPdfDoc(
  data: DailyReportResult,
  _companyName = 'VNS CONSTRUCTION',
) {
  const { jsPDF } = await import('jspdf');
  const autoTable = (await import('jspdf-autotable')).default;

  const { items, summary } = data;
  const company = await getCompanyDetails();
  // Landscape A4 for wide 8-column layout matching website
  const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });
  const pageWidth = doc.internal.pageSize.getWidth();

  // 1. Top Professional Black & White Letterhead (no background colors)
  const companyName = company.name || 'VNS Contruction';
  const locationText = [company.address, company.city].filter(Boolean).join(', ') || '90/14, VIP Thamarai Nagar, Kollampalayam, Erode-638002';
  const phoneVal = company.phone || '+91 9659611044';
  const emailVal = company.email || 'vnsconstruction@hotmail.com';
  const contactText = [phoneVal, emailVal].filter(Boolean).join('   |   ');

  doc.setFont('Helvetica', 'bold');
  doc.setFontSize(16);
  doc.setTextColor(0, 0, 0);
  doc.text(companyName, 36, 42);

  doc.setFont('Helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(40, 40, 40);
  doc.text(locationText, 36, 56);
  doc.text(contactText, 36, 68);

  doc.setDrawColor(0, 0, 0);
  doc.setLineWidth(1);
  doc.line(36, 78, pageWidth - 36, 78);

  // 2. Report Details Subheader
  let curY = 96;

  doc.setFont('Helvetica', 'bold');
  doc.setFontSize(13);
  doc.setTextColor(15, 23, 42);
  doc.text('CLIENT DAY BOOK — DAILY PROJECT REPORT', 36, curY);

  doc.setFont('Helvetica', 'normal');
  doc.setFontSize(8.5);
  doc.setTextColor(100, 116, 139);
  doc.text(
    `Generated on ${new Date().toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' })}`,
    pageWidth - 36,
    curY,
    { align: 'right' },
  );

  curY += 16;
  doc.setFontSize(9.5);
  doc.setFont('Helvetica', 'bold');
  doc.setTextColor(30, 41, 59);
  doc.text(`Client: ${summary.clientName}`, 36, curY);

  doc.setFont('Helvetica', 'normal');
  doc.setTextColor(71, 85, 105);
  doc.text(
    `Project: ${summary.projectName}   |   Location: ${summary.location || 'Site'}   |   Period: ${formatDate(summary.fromDate)} to ${formatDate(summary.toDate)}`,
    36,
    curY + 13,
  );

  // Currency note
  doc.setFontSize(8.5);
  doc.setTextColor(100, 116, 139);
  doc.text('All amounts in Indian Rupees (INR)', pageWidth - 36, curY + 13, { align: 'right' });

  // 3. Table Data (8 columns matching website: S.No, Date, Type, Mode, Particulars & Remarks, Receipt, Payable, Running Balance)
  const tableRows: Array<[string, string, string, string, string, string, string, string]> = [];
  let pdfIdx = 0;

  // Opening Balance Row if present
  if (summary.openingBalancePaise !== 0) {
    pdfIdx += 1;
    tableRows.push([
      String(pdfIdx),
      formatDate(summary.fromDate),
      'OPENING',
      '—',
      'Opening Balance (Prior Period Net Balance)',
      summary.openingBalancePaise > 0 ? formatAmount(summary.openingBalancePaise) : '—',
      summary.openingBalancePaise < 0 ? formatAmount(Math.abs(summary.openingBalancePaise)) : '—',
      formatAmount(summary.openingBalancePaise),
    ]);
  }

  for (const item of items) {
    pdfIdx += 1;
    tableRows.push([
      String(pdfIdx),
      formatDate(item.date),
      item.typeLabel.toUpperCase(),
      item.mode || '—',
      item.particulars,
      item.receiptPaise > 0 ? formatAmount(item.receiptPaise) : '—',
      item.payablePaise > 0 ? formatAmount(item.payablePaise) : '—',
      formatAmount(item.runningBalancePaise),
    ]);
  }

  // Totals Row
  tableRows.push([
    '',
    '',
    '',
    '',
    'TOTAL TRANSACTIONS',
    formatAmount(summary.totalReceiptsPaise) || '0.00',
    formatAmount(summary.totalPayablesPaise) || '0.00',
    '—',
  ]);

  // Closing Balance Row
  const netClosing = summary.closingBalancePaise;
  const balanceTitle = netClosing >= 0 ? 'CLOSING CREDIT BALANCE (SURPLUS)' : 'CLOSING DEBIT BALANCE (PAYABLE OUTSTANDING)';
  tableRows.push([
    '',
    '',
    '',
    '',
    balanceTitle,
    '',
    formatAmount(Math.abs(netClosing)) || '0.00',
    formatAmount(netClosing) || '0.00',
  ]);

  autoTable(doc, {
    startY: curY + 24,
    margin: { left: 36, right: 36, bottom: 40 },
    head: [[
      'S.NO',
      'DATE',
      'TYPE',
      'MODE',
      'PARTICULARS AND REMARKS',
      'RECEIPT AMOUNT',
      'PAYABLE AMOUNT',
      'RUNNING BALANCE',
    ]],
    body: tableRows,
    theme: 'plain',
    headStyles: {
      fillColor: false,
      textColor: [0, 0, 0],
      fontSize: 8.5,
      fontStyle: 'bold',
      halign: 'left',
      cellPadding: 5,
      lineWidth: { top: 1, bottom: 1 },
      lineColor: [0, 0, 0],
    },
    styles: {
      fontSize: 8,
      textColor: [0, 0, 0],
      fillColor: false,
      cellPadding: 4.5,
      lineColor: [200, 200, 200],
      lineWidth: 0.5,
      overflow: 'linebreak',
    },
    columnStyles: {
      0: { cellWidth: 36, halign: 'center' },
      1: { cellWidth: 68, halign: 'center' },
      2: { cellWidth: 58, halign: 'center' },
      3: { cellWidth: 64, halign: 'center' },
      4: { cellWidth: 'auto', halign: 'left' },
      5: { cellWidth: 95, halign: 'right', fontStyle: 'bold' },
      6: { cellWidth: 95, halign: 'right', fontStyle: 'bold' },
      7: { cellWidth: 100, halign: 'right', fontStyle: 'bold' },
    },
    alternateRowStyles: {
      fillColor: false,
    },
    didParseCell: (hookData) => {
      const isTotalRow = hookData.row.index === tableRows.length - 2;
      const isBalanceRow = hookData.row.index === tableRows.length - 1;

      hookData.cell.styles.fillColor = false;
      if (isTotalRow || isBalanceRow) {
        hookData.cell.styles.fontStyle = 'bold';
        hookData.cell.styles.textColor = [0, 0, 0];
        if (hookData.column.index === 4) {
          hookData.cell.styles.halign = 'right';
        }
      } else if (hookData.section === 'body') {
        hookData.cell.styles.textColor = [0, 0, 0];
      }
    },
    didDrawPage: (hookData) => {
      const pageCount = doc.getNumberOfPages();
      const pageHeight = doc.internal.pageSize.getHeight();
      doc.setDrawColor(200, 200, 200);
      doc.setLineWidth(0.5);
      doc.line(36, pageHeight - 26, pageWidth - 36, pageHeight - 26);
      doc.setFontSize(8);
      doc.setFont('Helvetica', 'normal');
      doc.setTextColor(80, 80, 80);
      doc.text(
        'VNS Contruction',
        36,
        pageHeight - 16,
      );
      doc.text(
        `Page ${hookData.pageNumber} of ${pageCount}`,
        pageWidth - 36,
        pageHeight - 16,
        { align: 'right' },
      );
    },
  });

  return doc;
}

/**
 * Export Client Day Book to PDF
 */
export async function exportDailyReportPdf(
  data: DailyReportResult,
  companyName = 'VNS CONSTRUCTION',
): Promise<void> {
  const doc = await buildDailyReportPdfDoc(data, companyName);
  const { summary } = data;
  const safeProject = (summary.projectName || 'Project').replace(/[^\w-]/g, '_');
  const safeFrom = summary.fromDate || 'AllTime';
  const safeTo = summary.toDate || 'Present';
  const fileName = `DayBook-${safeProject}-${safeFrom}_to_${safeTo}.pdf`;
  const blob = doc.output('blob');
  await saveFile(fileName, blob, 'application/pdf');
}

/**
 * Share Client Day Book PDF
 */
export async function shareDailyReportPdf(
  data: DailyReportResult,
  companyName = 'VNS CONSTRUCTION',
): Promise<void> {
  const doc = await buildDailyReportPdfDoc(data, companyName);
  const { summary } = data;
  const safeProject = (summary.projectName || 'Project').replace(/[^\w-]/g, '_');
  const safeFrom = summary.fromDate || 'AllTime';
  const safeTo = summary.toDate || 'Present';
  const fileName = `DayBook-${safeProject}-${safeFrom}_to_${safeTo}.pdf`;
  const blob = doc.output('blob');
  await shareFile(fileName, blob, `Client Day Book - ${summary.projectName || 'Project'}`);
}

